import { coerceArray } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Directive } from '@angular/core';
import { ofActionSuccessful, Store } from '@ngxs/store';
import cloneDeep from 'lodash-es/cloneDeep';
import uniqueId from 'lodash-es/uniqueId';
import { MonoTypeOperatorFunction, Observable, ObservableInput, of, OperatorFunction, pipe, throwError } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { InjectorProviderService } from 'src/app/_core/services/injector.provider.service';
import { StoreProviderService } from 'src/app/_core/services/store.provider.service';

import { Logger } from 'src/app/_core/tools/logger';
import { SubscriptionManager } from 'src/app/_core/tools/subscription-manager';

/**
 *
 * Howto use rxjs operator:
 *  Start before obs
 *  ```
 *   const obs$: Observable =  ....;
 *   const zoneName = 'uniqKeyForCurrentProcess';
 *   this.startLoadingZone(zoneName),
 *   obs$.pipe(
 *      tap(() => this.endLoadingZone())
 *      );
 *  ```
 *
 *  Start in observable
 *  ```
 *   of(null).pipe(
 *      this.startLoader(zoneName)
 *      switchMap(() => obs$),
 *      this.endLoader(zoneName)
 *      );
 *  ```
 *
 *  Wrap entire observable
 *  ```
 *   of(null).pipe(
 *      this.wrapLoader(obs$, zoneName)
 *      );
 *  ```
 * OU
 *  ```
 *    this.wrapLoader$(obs$, zoneName);
 *    ```
 */

// on doit ajouter ce décorateur pour qu'Ivy fonctionne
@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class BaseComponent extends SubscriptionManager {

    protected readonly className = this.constructor.name;

    protected readonly logger = new Logger(this.className);

    protected readonly store: Store;

    protected _loading = false;

    // stockage des différentes zones de chargement
    private _loadingZonesList = new Set<string>();

    constructor(protected cdr: ChangeDetectorRef) {
        super();
        this.logger.trace('create component')();
        this.store = InjectorProviderService.get(Store);
    }

    get loading(): boolean {
        return this._loading;
    }

    set loading(loading: boolean) {
        if (this._loadingZonesList.size > 0) {
            console.warn('you should not use set loading directly when using loadingZone');
        }
        this._loading = loading;
        this.cdr.markForCheck();
    }

    /**
     * est ce que la zone spécifiée est en cours de chargement ?
     */
    isZoneLoading(zoneName: string): boolean {
        return this._loadingZonesList.has(zoneName);
    }

    /**
     * opérateur rxjs qui démarre un loader nommé {zoneName}
     *  ```
     *   obs$.pipe(
     *      this.startLoader(zoneName)
     *      switchMap(() => obs$),
     *      this.endLoader(zoneName)
     *      );
     *  ```
     */
    protected startLoader<T>(zoneName: string): MonoTypeOperatorFunction<T> {
        return pipe(
            tap(() => this.startLoadingZone(zoneName))
        );
    }

    /**
     * opérateur rxjs qui termine un loader nommé {zoneName}
     * ajoute également directement un catch error pour arrêter le loading en cas d'erreur
     *  ```
     *   obs$.pipe(
     *      this.startLoader(zoneName)
     *      switchMap(() => obs$),
     *      this.endLoader(zoneName)
     *      );
     *  ```
     */
    protected endLoader<T>(zoneName: string): MonoTypeOperatorFunction<T> {
        return pipe(
            tap(
                () => this.endLoadingZone(zoneName),
                error => {
                    this.endLoadingZone(zoneName);
                    return throwError(error);
                }
            )
        );
    }

    /**
     * opérateur rxjs permettant de tracer le début et la fin de ses émissions d'un observable
     *  ```
     *   of(null).pipe(
     *      this.wrapLoader(obs$, zoneName)
     *      );
     *  ```
     */
    protected wrapLoader<Entree, Sortie>(project: (value: Entree, index?: number) => ObservableInput<Sortie>,
                                         zoneName: string
    ): OperatorFunction<Entree, Sortie> {
        return pipe(
            this.startLoader(zoneName),
            switchMap((v, i) => (project(v, i) as Observable<Sortie>).pipe(take(1))),
            this.endLoader(zoneName)
        );
    }

    /**
     * permet d'englober un observable pour tracer le début et la fin de ses émissions
     * ```
     * this.watch$ = this.wrapLoader$(obs$, zoneName);
     * ```
     */
    protected wrapLoader$<T>(obs$: Observable<T>, name: string = uniqueId()): Observable<T> {
        return of(true).pipe(
            this.wrapLoader(() => obs$, name)
        );
    }

    /**
     * démarre le loader nommé {zoneName}
     */
    protected startLoadingZone(zoneName: string): void {
        this.logger.trace()(`start zone ${zoneName} (${this._loadingZonesList.size} remain)`);
        // si il n'y a aucun loader en cours, le chargement démarre avec celui là
        if (this._loadingZonesList.size === 0) {
            this._loading = true;
        }
        this._loadingZonesList.add(zoneName);
        this.cdr.markForCheck();
    }

    /**
     * termine le loader nommé {zoneName}
     */
    protected endLoadingZone(zoneName: string): void {
        this._loadingZonesList.delete(zoneName);
        this.logger.trace()(`end zone ${zoneName} (${this._loadingZonesList.size} remain)`);
        // si il n'y a plus aucun loader en cours, le chargement est terminé
        if (this._loadingZonesList.size === 0) {
            this._loading = false;
        }
        this.cdr.markForCheck();
    }

    protected dispatch$(action: any | any[]): Observable<void> {
        return this.store.dispatch(action).pipe(this.untilDestroyed);
    }

    protected when$<T>(action: any | any[]): Observable<T> {
        action = coerceArray(action);
        return StoreProviderService.actions$.pipe(
            this.untilDestroyed,
            ofActionSuccessful(...action),
        );
    }

    protected snapshot<T>(selector: (state: any, ...states: any[]) => T, options?: { clone: boolean }): T {
        const snapshotData = this.store.selectSnapshot(selector);

        return options?.clone
            ? cloneDeep(snapshotData)
            : snapshotData;
    }

}
